import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getDefaultLandingPage } from '../../../config/permissionBasedNavigation';
import {
  Shield,
  Smartphone,
  RefreshCw,
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  HelpCircle
} from 'lucide-react';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

const TwoFactorVerify: React.FC = () => {
  const [token, setToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { isLoading: authLoading, user, hasPermission, userDomain } = useAuth();
  const navigate = useNavigate();

  // Check URL params for temp token (from email links), fall back to sessionStorage
  const urlParams = new URLSearchParams(window.location.search);
  const [tempToken, setTempToken] = useState(urlParams.get('token') || sessionStorage.getItem('2fa_temp_token') || '');
  const [email, setEmail] = useState(urlParams.get('email') || sessionStorage.getItem('2fa_email') || '');
  const [password, setPassword] = useState('');
  const [needsLogin, setNeedsLogin] = useState(!urlParams.get('token') && !sessionStorage.getItem('2fa_temp_token'));

  // Only redirect to dashboard if user was freshly authenticated (not from stale localStorage)
  // Don't redirect if we have a pending 2FA flow
  // If user is already authenticated (e.g. navigated here after login), redirect to dashboard
  useEffect(() => {
    if (user && !isLoading) {
      const landingPage = getDefaultLandingPage(hasPermission, userDomain);
      navigate(landingPage, { replace: true });
    }
  }, [user, isLoading, hasPermission, userDomain, navigate]);

  // Auto-verify if magic link was clicked (code + token in URL)
  const [autoVerifying, setAutoVerifying] = useState(false);
  useEffect(() => {
    const urlCode = urlParams.get('code');
    const urlToken = urlParams.get('token');
    if (urlCode && urlToken && !autoVerifying) {
      setAutoVerifying(true);
      setToken(urlCode);
      // Auto-submit after a brief render
      (async () => {
        try {
          const response = await fetch('/api/auth/verify-2fa', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': getCsrfToken(),
            },
            body: JSON.stringify({ tempToken: urlToken, twoFactorToken: urlCode }),
          });
          const result = await response.json();
          if (result.success && result.user) {
            localStorage.setItem('token', result.token || '');
            localStorage.setItem('refreshToken', result.refreshToken || '');
            localStorage.setItem('user', JSON.stringify(result.user));
            sessionStorage.removeItem('2fa_temp_token');
            sessionStorage.removeItem('2fa_email');
            window.location.href = '/login';
          } else {
            setError(result.message || 'Verification failed. Please enter the code manually.');
            setAutoVerifying(false);
          }
        } catch {
          setError('Auto-verification failed. Please enter the code manually.');
          setAutoVerifying(false);
        }
      })();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // If no temp token, show inline message instead of redirecting
  // (user may have arrived from an email link in a new tab)

  const handleLoginForCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const result = await response.json();
      if (result.success && result.requiresTwoFactor && result.tempToken) {
        setTempToken(result.tempToken);
        sessionStorage.setItem('2fa_temp_token', result.tempToken);
        sessionStorage.setItem('2fa_email', email);
        setNeedsLogin(false);
        setError('');
      } else if (result.success && result.user) {
        // No 2FA needed — direct login
        localStorage.setItem('token', result.token || '');
        localStorage.setItem('refreshToken', result.refreshToken || '');
        localStorage.setItem('user', JSON.stringify(result.user));
        window.location.href = '/';
        return;
      } else {
        setError(result.message || 'Login failed.');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tempToken) {
      setError('Session expired. Please log in again.');
      setNeedsLogin(true);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/verify-2fa', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({ tempToken, twoFactorToken: token }),
      });

      const result = await response.json();

      if (result.success && result.user) {
        // Store tokens and user data
        localStorage.setItem('token', result.token || '');
        localStorage.setItem('refreshToken', result.refreshToken || '');
        localStorage.setItem('user', JSON.stringify(result.user));

        // Clean up 2FA session data
        sessionStorage.removeItem('2fa_temp_token');
        sessionStorage.removeItem('2fa_email');

        // Redirect to login page — it detects the authenticated user
        // and redirects to the correct role-based dashboard
        window.location.href = '/login';
      } else {
        setError(result.message || 'Invalid verification code. Please try again.');
        setIsLoading(false);
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    sessionStorage.removeItem('2fa_temp_token');
    sessionStorage.removeItem('2fa_email');
    navigate('/login');
  };

  // Show loading while auto-verifying from magic link
  if (autoVerifying) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <RefreshCw className="text-blue-600 mx-auto mb-3 animate-spin" size={36} />
          <h2 className="text-xl font-bold text-slate-900">Verifying...</h2>
          <p className="text-slate-500 text-sm mt-1">Logging you in automatically</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <Shield className="text-blue-600 mx-auto mb-3" size={36} />
          <h1 className="text-3xl font-bold text-slate-900">Two-Factor Authentication</h1>
          <p className="text-slate-500 text-sm mt-1">
            {email
              ? `Enter the 6-digit code sent to ${email}`
              : 'Enter the 6-digit code from your email'}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-3xl border border-slate-100 p-8">
          {needsLogin ? (
            <form className="space-y-5" onSubmit={handleLoginForCode}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                <input id="email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  autoFocus />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                <input id="password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" />
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={16} />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
              <button type="submit" disabled={isLoading || !email || !password}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {isLoading ? (<><RefreshCw size={16} className="animate-spin" /> Sending code...</>) : 'Send Verification Code'}
              </button>
              <p className="text-xs text-slate-400 text-center">Enter your credentials to receive a 2FA code via email</p>
            </form>
          ) : (
          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* Token Input */}
            <div>
              <label htmlFor="token" className="block text-sm font-medium text-slate-700 mb-1.5">
                Verification Code
              </label>
              <div className="relative">
                <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  id="token"
                  name="token"
                  type="text"
                  required
                  maxLength={6}
                  pattern="[0-9]{6}"
                  placeholder="000000"
                  value={token}
                  onChange={(e) => setToken(e.target.value.replace(/\D/g, ''))}
                  className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-xl font-mono tracking-widest text-sm"
                  autoFocus
                />
              </div>
              <p className="text-xs text-slate-400 text-center mt-1.5">
                Check your email for a 6-digit verification code
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={16} />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || authLoading || token.length !== 6}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading || authLoading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Verify Code
                  <CheckCircle size={16} />
                </>
              )}
            </button>
          </form>
          )}

          {/* Action Buttons */}
          <div className="mt-5 flex items-center justify-between">
            <button
              type="button"
              onClick={handleBackToLogin}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft size={15} />
              Back to Login
            </button>
          </div>

          {/* Help Section */}
          <div className="mt-6 pt-5 border-t border-slate-200">
            <div className="flex items-start gap-2.5">
              <HelpCircle className="text-slate-400 shrink-0 mt-0.5" size={16} />
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-0.5">Need help?</h4>
                <p className="text-xs text-slate-500">
                  {needsLogin
                    ? 'Enter your credentials to receive a new verification code. If you already have a code, it was sent to your email.'
                    : 'Check your email for a 6-digit code. If you didn\'t receive it, go back to login and try again. The code expires after 10 minutes.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center mt-6 text-xs text-slate-400">
          &copy; 2026 EIS Tech. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default TwoFactorVerify;
